import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Box, Stack, Menu, MenuItem, Button, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { activeViewerWindow } from './store/documentViewerReducer';
import { TabItem } from './elements/tabItem';
import { useTranslation } from 'react-i18next';
import {
    AdminDoc,
    CopyDocId,
    DescriptionContent,
    Download,
    HideDoc,
    ShareDocIcon,
    TabsClose,
    TabsCloseInactive,
    TransferDoc,
} from './assets/icons';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '40px',
        'overflow-y': 'hidden',
        'overflow-x': 'auto',
        paddingTop: '3px',
        paddingLeft: '0px',
        paddingRight: '0px',
        scrollbarWidth: '5px',
        scrollbarHeight: '5px',
        scrollbarColor: `${theme.palette.documentViewer.lowContrastTextColor} transparent`,
        '&::-webkit-scrollbar-thumb:hover': {
            background: `${theme.palette.documentViewer.lowContrastTextHoverColor} !important`,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.documentViewer.lowContrastTextColor,
        },
    },
}));

export function ViewerTabs(props) {
    const classes = useStyles();
    const { t } = useTranslation();
    const tabRefRow = React.useRef(null);
    const ITEM_HEIGHT = 48;
    const tabs = props.openTabs;
    const activeViewerWindowIdx = useSelector(activeViewerWindow);
    const activeTab = props.selectedTabs[activeViewerWindowIdx];
    const [tabAnchor, setTabAnchor] = React.useState(null);
    const showTabContextMenu = Boolean(tabAnchor);
    const openContextMenu = (event) => {
        setTabAnchor(event.currentTarget);
    };
    const [tabBarAnchor, setTabBarAnchor] = React.useState(null);
    const showTabBarContextMenu = Boolean(tabBarAnchor);
    const openRestOfTabs = (event) => {
        setTabBarAnchor(event.currentTarget);
    };
    const closeContextMenu = () => {
        setTabAnchor(null);
        setTabBarAnchor(null);
    };
    const options = [
        { action: 'closeAll', icon: <TabsClose />, display: t('Close All') },
        { action: 'closeAllExcept', icon: <TabsCloseInactive />, display: t('Close All Except This') },
        { action: 'getShareLink', icon: <ShareDocIcon />, display: t('Get Share Link') },
        { action: 'downloadDoc', icon: <Download />, display: t('Download Document') },
        { action: 'copyDocId', icon: <CopyDocId />, display: t('Copy Document ID') },
        { action: 'adminDoc', icon: <AdminDoc />, display: t('Admin Document') },
        { action: 'transferDoc', icon: <TransferDoc />, display: t('Transfer Document') },
        { action: 'transferToDist', icon: <TransferDoc />, display: t('Transfer To Distribution') },
        { action: 'hideDoc', icon: <HideDoc />, display: t('Hide Document') },
    ];
    const setActTab = (tabId) => {
        setTabAnchor(null);
        if (props.changeActiveTab) props.changeActiveTab(tabId);
    };
    const closeTab = (tabId) => {
        setTabAnchor(null);
        if (props.closeTab) props.closeTab(tabId);
    };
    const [windowWidth, setWindowWidth] = React.useState(0);
    const [tabsToShow, setTabsToShow] = React.useState([]);
    const [tabsOnMenu, setTabsOnMenu] = React.useState([]);
    React.useEffect(() => {
        const settingWidth = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', settingWidth);
        return () => window.removeEventListener('resize', settingWidth);
    }, []);
    React.useEffect(() => {
        const spaceAvailable = tabRefRow.current.offsetWidth;
        const forHowManyHaveSpace = Math.floor((spaceAvailable - 50) / 240);
        if (forHowManyHaveSpace >= tabs.length) {
            setTabsToShow(_.cloneDeep(tabs));
            setTabsOnMenu([]);
        } else {
            setTabsToShow(_.cloneDeep(tabs).slice(0, forHowManyHaveSpace));
            setTabsOnMenu(_.cloneDeep(tabs).slice(forHowManyHaveSpace - tabs.length));
        }
    }, [tabs, windowWidth]);
    //tab has static 240px width // count how many  can be and
    //for rest add dropdown to select other

    return (
        <>
            <Stack direction={'row'} className={classes.root} ref={tabRefRow}>
                {tabsToShow.map((tab) => {
                    return (
                        <TabItem
                            key={`tab-of-document-${tab.tabId}`}
                            tabId={tab.tabId}
                            name={tab.tabName}
                            selected={activeTab === tab.tabId}
                            onMenu={openContextMenu}
                            onSelect={setActTab}
                            onClose={closeTab}
                        />
                    );
                })}
                {tabsOnMenu.length > 0 && (
                    <Box paddingX={'5px'} style={{ background: '#494B4C', borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}>
                        <Button
                            variant="contained"
                            onClick={openRestOfTabs}
                            size={'small'}
                            style={{ padding: '3px 5px', minWidth: '26px' }}
                        >
                            +{tabsOnMenu.length}
                        </Button>
                    </Box>
                )}
            </Stack>
            <Menu
                id="context-menu-action"
                anchorEl={tabAnchor}
                open={showTabContextMenu}
                onClose={closeContextMenu}
                slotProps={{
                    paper: {
                        style: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                            width: '20ch',
                        },
                    },
                    list: {
                        'aria-labelledby': 'context-menu-action-button',
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option.action} onClick={closeContextMenu}>
                        <Stack direction={'row'} gap={'6px'}>
                            {option.icon}
                            {option.display}
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
            <Menu
                id="tabs-hidden-menu"
                anchorEl={tabBarAnchor}
                open={showTabBarContextMenu}
                onClose={closeContextMenu}
                slotProps={{
                    paper: {
                        style: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                            width: '20ch',
                        },
                    },
                    list: {
                        'aria-labelledby': 'tabs-hidden-button',
                    },
                }}
            >
                {tabsOnMenu.map((tabOnMenu) => (
                    <MenuItem key={'tab-' + tabOnMenu.tabId} selected={tabOnMenu.tabId === activeTab}>
                        <Stack
                            direction={'row'}
                            gap={'6px'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            style={{ width: 'calc(100% + 10px)', marginRight: '-10px' }}
                        >
                            <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} className={classes.tabContent}>
                                <DescriptionContent color={'#FFE485'} style={{ marginLeft: '-5px', marginRight: '5px' }} />
                                <Box>{`${tabOnMenu.tabName} (${moment.isMoment(tabOnMenu.tabDocDate) ? tabOnMenu.tabDocDate.format('DD/MM/YYYY HH:mm') : 'no date'}), ${tabOnMenu.toolbar.pagesCount} ${tabOnMenu.toolbar.pagesCount > 1 ? 'Pages' : 'Page'}`}</Box>
                            </Stack>
                            <IconButton aria-label="menu" className={classes.tabIconButton} onClick={openContextMenu}>
                                <MoreVertIcon size={'20px'} />
                            </IconButton>
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
